import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ChangingPassword: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/help')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('help.backToHelp', 'Back to Help Center')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          ACCOUNT MANAGEMENT
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Changing Your Password
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Keeping your IndeeClips account secure starts with a strong, unique password. Regularly updating your password and following security best practices helps protect your channel, personal information, and content from unauthorized access.
        </Typography>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          How to Change Your Password
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          If you're currently signed in and know your existing password:
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Sign in to your IndeeClips account and navigate to your profile page
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Click on "Account Settings" or "Security Settings"
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Look for the "Change Password" section
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Enter your current password for verification
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Type your new password (must meet security requirements)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Confirm your new password by typing it again
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Click "Save" or "Update Password" to complete the change
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Resetting a Forgotten Password
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          If you've forgotten your password and cannot sign in:
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Go to the IndeeClips login page
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Click "Forgot Password?" below the password field
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Enter the email address associated with your account
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Check your email inbox for a password reset link (check spam folder if not received)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Click the reset link within 24 hours (links expire for security)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Create a new password that meets security requirements
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Sign in with your new password to verify it works
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Creating a Strong Password
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Use at least 12 characters:</strong> Longer passwords are significantly harder to crack
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Mix character types:</strong> Include uppercase letters, lowercase letters, numbers, and special symbols
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Avoid common words:</strong> Don't use dictionary words, names, or obvious patterns like "123456"
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Make it unique:</strong> Don't reuse passwords from other websites or services
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Consider a passphrase:</strong> Combine random words into a memorable but unpredictable phrase
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            <strong>Use a password manager:</strong> Consider storing complex passwords securely in a reputable password manager app
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Security Best Practices
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Change your password immediately if you suspect unauthorized access to your account
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Never share your password with anyone, including people claiming to be IndeeClips support staff
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Update your password every 3-6 months as a preventive measure
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Avoid using the same password for IndeeClips and other online services
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Be cautious of phishing emails asking you to click links and enter your password
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Sign out of your account when using shared or public computers
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Troubleshooting Password Reset Issues
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          If you don't receive the password reset email, check your spam or junk folder. Ensure you entered the correct email address associated with your account. If problems persist, wait a few minutes and try requesting another reset link. Contact support if you continue experiencing issues after multiple attempts.
        </Typography>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Can't Access Your Account?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            If you're unable to reset your password or regain access to your account, our support team can help verify your identity and restore access. Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ChangingPassword;
